import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import styles from './CourseCard.module.scss'
import {
    NOTECARD_BLURB,
    OUTLINE_BLURB,
    PRACTICE_PROBLEM_BLURB,
} from '@/utils/marketingPhrases'
import { CourseInfo, Instructor } from 'common/src/api/core/courses/types'
import { flatten, uniq } from 'lodash'
import { INSTRUCTOR_TO_INSTRUCTOR_DATA } from '@/components/Courses/InstructorModal/instructorData'
import {
    CTAButton,
    CTAButtonType,
} from '@/components/static/ui/Button/CTAButton/CTAButton'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import {
    GenericButton,
    GenericButtonType,
} from '@/components/static/ui/Button/GenericButton/GenericButton'
import {
    AccessType,
    ALL_ACCESS_TYPES,
    PaymentDetail,
} from 'common/src/api/websiteFrontendVsWebsiteBackend/accountMaintenance/types'
import { getPrettyFormattedDateTextForDate } from '@/components/static/SplashPage/dateUtils'
import {
    UserInfoResponse,
    UserPaymentHistoryResponse,
} from 'common/src/api/websiteFrontendVsWebsiteBackend/accountMaintenance/responses'
import {
    AuthenticatorOpenPurpose,
    AuthenticatorRoutingProps,
} from '@/components/static/Authenticator/Authenticator.types'
import { useRouter } from 'next/router'
import notification from 'antd/lib/notification'
import { NotificationKeys } from '@/utils/notificationKeys'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { addDemoCourse } from '@/api/accountMaintenance'
import { ALREADY_OWNED_STRING } from '@/utils/constants'
import { CourseCardItem } from '@/components/Courses/CourseCard/CourseCardItem/CourseCardItem'
import { Popover } from 'antd'
import { PassGuaranteeComponent } from '@/components/Courses/PassGuaranteeComponent/PassGuaranteeComponent'
import {
    CourseConfig,
    CourseConfigType,
    getCourseConfig,
} from 'common/src/courseConfig'
import CountUp from 'react-countup'

interface CourseCardProps {
    courseInfo: CourseInfo
    setActiveInstructor: (instructor: Instructor) => void
    isLoggedIn: boolean
    userPaymentHistory: UserPaymentHistoryResponse | null
    demoHistory: { courseName: string; isActive: boolean }[] | null
    setIsLoginModalVisible: (isLoginModalVisible: boolean) => void
    setLoginModalRoutingPurpose: (
        loginModalRoutingPurpose: AuthenticatorRoutingProps
    ) => void
    setUserInfo: (userInfo: UserInfoResponse) => void | null
    courseConfigType: CourseConfigType
}

export const CourseCard: React.FC<CourseCardProps> = (props): ReactElement => {
    const router = useRouter()
    const courseConfig = useMemo((): CourseConfig => {
        return getCourseConfig(props.courseConfigType)
    }, [props.courseConfigType])
    const paymentHistoryResponse = props.userPaymentHistory
    const [accessTypesToPurchase, setAccessTypesToPurchase] =
        useState<AccessType[]>(ALL_ACCESS_TYPES)
    const [accessTypesPurchased, setAccessTypesPurchased] =
        useState<AccessType[]>(ALL_ACCESS_TYPES)
    const isValidNextSittingDate = useMemo((): boolean => {
        return props.courseInfo.nextSittingDate > new Date()
    }, [props.courseInfo.nextSittingDate])
    useEffect(() => {
        let paymentsForCourse: PaymentDetail[]
        if (!paymentHistoryResponse) {
            paymentsForCourse = []
        } else {
            paymentsForCourse = paymentHistoryResponse.paymentHistory.filter(
                (pmt) => pmt.courseName === props.courseInfo.name && !pmt.isDemo
            )
        }
        const accessTypesPurchased = uniq(
            flatten(
                paymentsForCourse.map((pmt) => {
                    if ('accessTypes' in pmt) {
                        return pmt.accessTypes
                    }
                })
            )
        )
        setAccessTypesPurchased(accessTypesPurchased)

        const accessTypesToPurchase = ALL_ACCESS_TYPES.filter(
            (accessType) => !accessTypesPurchased.includes(accessType)
        )
        setAccessTypesToPurchase(accessTypesToPurchase)
    }, []) // intentionally no dependencies, do on mount

    const hasCourseBeenPurchasedWithFullAccess = useMemo(():
        | { purchased: false }
        | { purchased: true; fullAccess: boolean } => {
        if (!paymentHistoryResponse) {
            return { purchased: false }
        }
        const paymentsForCourse = paymentHistoryResponse.paymentHistory.filter(
            (pmt) => pmt.courseName === props.courseInfo.name && !pmt.isDemo
        )
        if (paymentsForCourse.length === 0) {
            return { purchased: false }
        }
        const accessTypes = uniq(
            flatten(
                paymentsForCourse.map((pmt) => {
                    if ('accessTypes' in pmt) {
                        return pmt.accessTypes
                    }
                })
            )
        )
        return {
            purchased: true,
            fullAccess: accessTypes.length === ALL_ACCESS_TYPES.length,
        }
    }, [paymentHistoryResponse, props.courseInfo.name])

    const isDemoAlreadyUsed = useMemo(() => {
        if (!props.demoHistory) {
            return false
        }
        return !!props.demoHistory.find(
            (demo) => demo.courseName === props.courseInfo.name
        )
    }, [props])

    const [isBuyNowLoading, setIsBuyNowLoading] = useState<boolean>(false)
    const [isDemoLoading, setIsDemoLoading] = useState<boolean>(false)

    const buyNowButton = useMemo((): ReactElement => {
        if (
            hasCourseBeenPurchasedWithFullAccess.purchased &&
            hasCourseBeenPurchasedWithFullAccess.fullAccess
        ) {
            return (
                <GenericButton
                    type={GenericButtonType.primaryDarkBackground}
                    text={ALREADY_OWNED_STRING}
                    onClick={null}
                    disabled={true}
                />
            )
        }

        return (
            <CTAButton
                type={CTAButtonType.primaryDarkBackground}
                loading={isBuyNowLoading}
                disabled={accessTypesToPurchase.length === 0}
                onClick={async () => {
                    try {
                        setIsBuyNowLoading(true)
                        if (props.isLoggedIn) {
                            return await router.push({
                                pathname: UiLayoutConstant.COURSE_PAYMENT_PAGE,
                                query: {
                                    courseName: props.courseInfo.name,
                                    accessTypes: accessTypesToPurchase,
                                },
                            })
                        }
                        notification.info({
                            message: 'One last thing.',
                            description:
                                'Please sign up or log in before proceeding with your purchase.',
                            key: NotificationKeys.MUST_LOGIN_TO_PURCHASE,
                            duration: null,
                        })

                        props.setLoginModalRoutingPurpose({
                            purpose: AuthenticatorOpenPurpose.PURCHASE,
                            courseName: props.courseInfo.name,
                            accessTypes: accessTypesToPurchase,
                        })
                        props.setIsLoginModalVisible(true)
                    } finally {
                        setIsBuyNowLoading(false)
                    }
                }}
                text={'Buy Now'}
            />
        )
    }, [
        accessTypesToPurchase,
        hasCourseBeenPurchasedWithFullAccess,
        isBuyNowLoading,
        props,
        router,
    ])

    const demoButton = useMemo((): ReactElement => {
        if (
            isDemoAlreadyUsed ||
            hasCourseBeenPurchasedWithFullAccess.purchased
        ) {
            return (
                <GenericButton
                    type={GenericButtonType.greenNoBackground}
                    text={'Demo Used'}
                    onClick={null}
                    disabled={true}
                />
            )
        }

        return (
            <CTAButton
                type={CTAButtonType.defaultBorderDarkBackground}
                loading={isDemoLoading}
                onClick={async () => {
                    try {
                        setIsDemoLoading(true)
                        if (props.isLoggedIn) {
                            const response = await addDemoCourse(
                                props.courseInfo.name
                            )
                            if (!response.data.success) {
                                notification.error({
                                    message: 'Something went wrong',
                                    description:
                                        'Please try again or reach out to support@justenoughprep.com',
                                    key: NotificationKeys.ERROR_CREATING_DEMO,
                                    duration: null,
                                })
                                return
                            }
                            props.setUserInfo(response.data.payload)
                            return await router.push({
                                pathname: UiLayoutConstant.COURSE_DEMO_PAGE,
                                query: { courseName: props.courseInfo.name },
                            })
                        }

                        notification.info({
                            message: 'One last thing.',
                            description:
                                'Please sign up or log in before proceeding with your demo.',
                            key: NotificationKeys.MUST_LOGIN_TO_DEMO,
                            duration: null,
                        })

                        props.setLoginModalRoutingPurpose({
                            purpose: AuthenticatorOpenPurpose.DEMO,
                            courseName: props.courseInfo.name,
                            accessTypes: null,
                        })
                        props.setIsLoginModalVisible(true)
                    } finally {
                        setIsDemoLoading(false)
                    }
                }}
                text={'Free Trial'}
            />
        )
    }, [
        hasCourseBeenPurchasedWithFullAccess.purchased,
        isDemoAlreadyUsed,
        isDemoLoading,
        props,
        router,
    ])

    const informationContents = useMemo((): ReactElement => {
        return (
            <div>
                <CourseCardItem
                    accessType={AccessType.PRACTICE_PROBLEMS}
                    accessTypesPurchased={accessTypesPurchased}
                    accessTypesToPurchase={accessTypesToPurchase}
                    setAccessTypesToPurchase={setAccessTypesToPurchase}
                    costPerAccessType={
                        props.courseInfo.price / ALL_ACCESS_TYPES.length
                    }
                    bulletItems={
                        <ul>
                            <div className={styles.bulletItem}>
                                <li>
                                    {
                                        props.courseInfo.contentStats
                                            .numPracticeProblems
                                    }{' '}
                                    {PRACTICE_PROBLEM_BLURB}
                                </li>
                            </div>

                            {courseConfig.includesExcelTemplates && (
                                <div className={styles.bulletItem}>
                                    <li>
                                        Includes Excel templates and solutions
                                    </li>
                                </div>
                            )}
                            <div className={styles.bulletItem}>
                                <li>Custom quiz and exam builders</li>
                            </div>
                        </ul>
                    }
                />
                <CourseCardItem
                    accessType={AccessType.NOTECARDS}
                    accessTypesPurchased={accessTypesPurchased}
                    accessTypesToPurchase={accessTypesToPurchase}
                    setAccessTypesToPurchase={setAccessTypesToPurchase}
                    costPerAccessType={
                        props.courseInfo.price / ALL_ACCESS_TYPES.length
                    }
                    bulletItems={
                        <ul>
                            <div className={styles.bulletItem}>
                                <li>
                                    {props.courseInfo.contentStats.numNotecards}{' '}
                                    {NOTECARD_BLURB}
                                </li>
                            </div>
                            <div className={styles.bulletItem}>
                                <li>
                                    Customizable decks with varying shuffle
                                    algorithms
                                </li>
                            </div>
                        </ul>
                    }
                />
                <CourseCardItem
                    accessType={AccessType.OUTLINES}
                    accessTypesPurchased={accessTypesPurchased}
                    accessTypesToPurchase={accessTypesToPurchase}
                    setAccessTypesToPurchase={setAccessTypesToPurchase}
                    costPerAccessType={
                        props.courseInfo.price / ALL_ACCESS_TYPES.length
                    }
                    bulletItems={
                        <ul>
                            <div className={styles.bulletItem}>
                                <li>{OUTLINE_BLURB}</li>
                            </div>
                            <div className={styles.bulletItem}>
                                <li>
                                    Embedded practice problems to master
                                    concepts
                                </li>
                            </div>
                        </ul>
                    }
                />
            </div>
        )
    }, [
        accessTypesPurchased,
        accessTypesToPurchase,
        courseConfig.includesExcelTemplates,
        props.courseInfo.contentStats.numNotecards,
        props.courseInfo.contentStats.numPracticeProblems,
        props.courseInfo.price,
    ])

    return (
        <div className={styles.courseCard}>
            <div className={styles.titleContainer}>
                <div className={styles.courseNameAndOrganization}>
                    <h1 className={styles.courseName}>
                        {`Exam ${props.courseInfo.abbreviation}: ${props.courseInfo.prettyName}`}
                    </h1>
                    <h1
                        className={styles.organization}
                    >{`Org: ${props.courseInfo.organization} | ${props.courseInfo.track}`}</h1>
                    {isValidNextSittingDate && (
                        <div className={styles.nextExamInformation}>
                            {`Next Sitting: ${getPrettyFormattedDateTextForDate(
                                props.courseInfo.nextSittingDate
                            )}`}
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.subHeaderContainer}>
                <div className={styles.title}>Choose your course materials</div>
            </div>
            <div className={styles.informationContainer}>
                {informationContents}
                <div className={styles.passGuarantee}>
                    All prices include our forever pass guarantee
                    <Popover
                        content={<PassGuaranteeComponent />}
                        trigger={['hover', 'focus', 'click']}
                        overlayClassName={styles.popover}
                    >
                        <span className={styles.footnote}>1</span>
                    </Popover>
                </div>
            </div>
            {!(
                hasCourseBeenPurchasedWithFullAccess.purchased &&
                hasCourseBeenPurchasedWithFullAccess.fullAccess
            ) && (
                <>
                    <hr />
                    <div className={styles.totalContainer}>
                        <div>TOTAL</div>
                        <div className={styles.price}>
                            $
                            <CountUp
                                end={
                                    (accessTypesToPurchase.length *
                                        props.courseInfo.price) /
                                    ALL_ACCESS_TYPES.length
                                }
                                duration={0.3}
                                decimals={0}
                                preserveValue={true}
                            />
                            {/*<AnimatedNumber*/}
                            {/*    value={*/}
                            {/*        (accessTypesToPurchase.length **/}
                            {/*            props.courseInfo.price) /*/}
                            {/*        ALL_ACCESS_TYPES.length*/}
                            {/*    }*/}
                            {/*    animationDurationInMS={300}*/}
                            {/*    precision={0}*/}
                            {/*/>*/}
                        </div>
                    </div>
                </>
            )}
            <div className={styles.ctaContainer}>
                <div>{buyNowButton}</div>
                <div className={styles.splitter}>- or -</div>
                <div>{demoButton}</div>
            </div>
            <div className={styles.instructorContainer}>
                <div className={styles.picture}>
                    {
                        INSTRUCTOR_TO_INSTRUCTOR_DATA[
                            props.courseInfo.instructor
                        ].imageComponent
                    }
                </div>
                <div className={styles.information}>
                    <div className={styles.instructorHeader}>Instructor</div>
                    <div className={styles.instructorName}>
                        {
                            INSTRUCTOR_TO_INSTRUCTOR_DATA[
                                props.courseInfo.instructor
                            ].prettyName
                        }
                    </div>
                    <div
                        className={`${reusableCssClass.clickableText} ${styles.instructorCTA}`}
                        onClick={() =>
                            props.setActiveInstructor(
                                props.courseInfo.instructor
                            )
                        }
                    >
                        Learn More
                    </div>
                </div>
            </div>
        </div>
    )
}
